/**
 * Client copy of functions/grandfathering.js entitlement rules.
 * Legacy open subscriptions keep £9.99 / 600, £15.99 / 5,000, or £29.99 / unlimited.
 */

const LEGACY_PRICE_TO_PLAN = {
  price_1SWPW7G7Fe7FIGiRzheFwiaJ: 'starter',
  price_1SWPWhG7Fe7FIGiRpOWWiz4X: 'growthpro',
  price_1SWPXAG7Fe7FIGiRiCwdNsgy: 'enterprise',
  price_1Tf6TnG7Fe7FIGiRm7j6SDv8: 'enterprise',
  price_1Rs2LrG7Fe7FIGiRbNcHpZms: 'starter',
  price_1Rs2LmG7Fe7FIGiRMEZLvMuN: 'growthpro',
  price_1Rs2LeG7Fe7FIGiRrzMJ2y8W: 'enterprise',
  price_1Rk5Rl4eiPLbY4sD7HuGAr62: 'starter',
  price_1Rk5Sn4eiPLbY4sDdqxS4e8J: 'growthpro',
  price_1Rk5TW4eiPLbY4sDlmwGDZH3: 'enterprise',
};

const LEGACY_LIMITS = { starter: 600, growthpro: 5000, growth: 5000, enterprise: -1 };
const LEGACY_NAMES = { starter: 'Starter', growthpro: 'Growth Pro', growth: 'Growth Pro', enterprise: 'Enterprise' };
const LEGACY_PENCE = { starter: 999, growthpro: 1599, growth: 1599, enterprise: 2999 };
const CURRENT_LIMITS = { starter_v2: 100, professional: 500, premium: 1500 };
const CURRENT_NAMES = { starter_v2: 'Starter', professional: 'Professional', premium: 'Premium' };
const CURRENT_PENCE = { starter_v2: 1999, professional: 3999, premium: 6999 };
const LEGACY_STORED_TO_REPLACE = new Set([50, 100, 200, 1000]);
const OPEN_STATUSES = new Set(['active', 'trialing', 'past_due', 'unpaid', 'trial']);

const PROTECTED_STRIPE_SUBSCRIPTION_IDS = new Set([
  'sub_1ULleEG7Fe7FIGiRIGptFDht',
  'sub_1TmW9mG7Fe7FIGiRjfthX0FU',
  'sub_1TPNM7G7Fe7FIGiRc26LQg1u',
  'sub_1TL5c6G7Fe7FIGiRCF8jVeWu',
  'sub_1TG7LJG7Fe7FIGiRmpDHMTgi',
  'sub_1TG2WFG7Fe7FIGiRyWu5H9SV',
  'sub_1TEye8G7Fe7FIGiRV4VR78eC',
  'sub_1T3gIgG7Fe7FIGiRbcxlm4Cb',
]);

const PROTECTED_CLINIC_IDS = new Set([
  'Dn8ZCIQUJyDluvVSuWZo',
  '8wpX9SlXOdmHFbmmO63M',
  '0eAGxJHLDupwT5az36ZR',
  'GXOiywsnIhv9dLga2WfK',
  'jnC5hnJUlaW6qwSjnRE8',
  'brQDIo3MhoVBiiQxSgdj',
  'cCXrE8TnU9DB2p3PFJ13',
]);

export function normalizePlanKey(raw) {
  if (raw == null || typeof raw === 'object') return null;
  const s = String(raw).trim().toLowerCase().replace(/\s+/g, '');
  if (!s || s === 'unknown') return null;
  if (s === 'starter_v2' || s === 'starter-v2' || s === 'starterv2') return 'starter_v2';
  if (s === 'professional') return 'professional';
  if (s === 'premium') return 'premium';
  if (s.includes('growth')) return 'growthpro';
  if (s.includes('enterprise')) return 'enterprise';
  if (s === 'starter' || s === 'basic') return 'starter';
  return s;
}

function isLegacyPlanKey(key) {
  return key === 'starter' || key === 'growthpro' || key === 'growth' || key === 'enterprise';
}

function isCurrentPlanKey(key) {
  return Object.prototype.hasOwnProperty.call(CURRENT_LIMITS, key);
}

function legacyPlanKey(key) {
  if (key === 'growth') return 'growthpro';
  return isLegacyPlanKey(key) ? key : 'starter';
}

function parseSubscriptionDate(value) {
  if (value == null) return null;
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  if (typeof value === 'object' && typeof value._seconds === 'number') return new Date(value._seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isSubscriptionOpen(subscription, now = new Date()) {
  if (!subscription) return false;
  const status = String(subscription.status || '').toLowerCase();
  if (OPEN_STATUSES.has(status)) return true;
  if (status === 'canceled' || status === 'cancelled' || status === 'cancel_at_period_end') {
    const end = parseSubscriptionDate(subscription.currentPeriodEnd);
    return !!(end && end.getTime() > now.getTime());
  }
  return false;
}

export function isProtectedSubscription(subscription, now = new Date(), clinicId = null) {
  if (clinicId && PROTECTED_CLINIC_IDS.has(clinicId) && isSubscriptionOpen(subscription, now)) return true;
  if (!subscription) return false;
  if (subscription.grandfathered === false && subscription.grandfatheringEndedAt) return false;
  const subId = subscription.stripeSubscriptionId || subscription.grandfatheredSubscriptionId;
  if (subId && PROTECTED_STRIPE_SUBSCRIPTION_IDS.has(subId) && isSubscriptionOpen(subscription, now)) return true;
  if (subscription.grandfathered === true && isSubscriptionOpen(subscription, now)) return true;
  if (!isSubscriptionOpen(subscription, now)) return false;
  if (subscription.stripePriceId && LEGACY_PRICE_TO_PLAN[subscription.stripePriceId]) return true;
  const key = normalizePlanKey(subscription.plan || subscription.planName || subscription.planType);
  return isLegacyPlanKey(key);
}

export function effectiveMonthlyAllowance(subscription = {}) {
  if (typeof subscription.lockedMonthlyConsentLimit === 'number') return subscription.lockedMonthlyConsentLimit;
  const key = normalizePlanKey(subscription.plan || subscription.planName || subscription.planType);
  if (isCurrentPlanKey(key)) {
    const stored = subscription.monthlyConsentLimit;
    if (typeof stored === 'number' && stored !== 0) return stored;
    return CURRENT_LIMITS[key];
  }
  const legacyKey = legacyPlanKey(key);
  const canonical = LEGACY_LIMITS[legacyKey];
  const stored = subscription.monthlyConsentLimit;
  if (stored === -1 || legacyKey === 'enterprise') return -1;
  if (typeof stored === 'number' && stored !== 0) {
    if (LEGACY_STORED_TO_REPLACE.has(stored) && stored !== canonical) return canonical;
    return stored;
  }
  return canonical;
}

function formatPence(pence) {
  return `£${((Number(pence) || 0) / 100).toFixed(2)}`;
}

function isPrePlanSubscription(subscription = {}) {
  if (!subscription) return true;
  if (subscription.accessOverride === true) return false;
  if (subscription.grandfathered === true) return false;
  if (subscription.stripeSubscriptionId) return false;
  const status = String(subscription.status || '').toLowerCase();
  return (
    !status ||
    status === 'none' ||
    status === 'incomplete' ||
    status === 'incomplete_expired'
  );
}

export function resolveEntitlement(subscription = {}, now = new Date(), clinicId = null) {
  if (isPrePlanSubscription(subscription)) {
    const stored = subscription.monthlyConsentLimit;
    return {
      protected: false,
      prePlan: true,
      planKey: null,
      planName: 'No plan',
      monthlyConsentLimit: typeof stored === 'number' ? stored : 0,
      pricePence: null,
      priceLabel: '—',
    };
  }

  const planKey = normalizePlanKey(subscription.plan || subscription.planName || subscription.planType);
  if (isProtectedSubscription(subscription, now, clinicId)) {
    const key = legacyPlanKey(isLegacyPlanKey(planKey) ? planKey : subscription.lockedPlanKey || 'starter');
    const limit = typeof subscription.lockedMonthlyConsentLimit === 'number'
      ? subscription.lockedMonthlyConsentLimit
      : effectiveMonthlyAllowance(subscription);
    const pence = typeof subscription.lockedUnitAmount === 'number' ? subscription.lockedUnitAmount : LEGACY_PENCE[key];
    return {
      protected: true,
      prePlan: false,
      planKey: subscription.lockedPlanKey || key,
      planName: subscription.lockedPlanName || LEGACY_NAMES[key] || 'Starter',
      monthlyConsentLimit: limit,
      pricePence: pence,
      priceLabel: formatPence(pence),
    };
  }
  if (isCurrentPlanKey(planKey)) {
    const stored = subscription.monthlyConsentLimit;
    const limit = typeof stored === 'number' && stored !== 0 ? stored : CURRENT_LIMITS[planKey];
    return {
      protected: false,
      prePlan: false,
      planKey,
      planName: CURRENT_NAMES[planKey],
      monthlyConsentLimit: limit,
      pricePence: CURRENT_PENCE[planKey],
      priceLabel: formatPence(CURRENT_PENCE[planKey]),
    };
  }
  if (!planKey) {
    const stored = subscription.monthlyConsentLimit;
    return {
      protected: false,
      prePlan: true,
      planKey: null,
      planName: 'No plan',
      monthlyConsentLimit: typeof stored === 'number' ? stored : 0,
      pricePence: null,
      priceLabel: '—',
    };
  }
  const key = legacyPlanKey(planKey);
  return {
    protected: false,
    prePlan: false,
    planKey: key,
    planName: LEGACY_NAMES[key] || 'Starter',
    monthlyConsentLimit: effectiveMonthlyAllowance(subscription),
    pricePence: LEGACY_PENCE[key],
    priceLabel: formatPence(LEGACY_PENCE[key]),
  };
}

export function allowanceLabel(limit) {
  if (limit === -1) return 'Unlimited';
  if (typeof limit !== 'number') return '600';
  return limit.toLocaleString('en-GB');
}
